/**
 * Growing the trade graph out of the province table.
 *
 * EU4 authors its eighty nodes and their arrows by hand, over years. This realm
 * is generated from a seed and is about to grow again, so the node graph has to
 * be grown too — and it has to come out the same on every machine, in every
 * build, with no `Math.random` anywhere near it. Everything below is a function
 * of the province table and the province graph and nothing else.
 *
 * Four steps.
 *
 * 1. **Seats.** The best market towns, spread out. Wealth plus a heavy thumb on
 *    the coast, taken greedily under a minimum separation so two markets are
 *    never neighbours.
 * 2. **Catchments.** Every province joins its nearest seat, breadth-first from
 *    all seats at once. Ties go to the lower node index, which is arbitrary and,
 *    much more importantly, the same every time.
 * 3. **Depth.** Hops to salt water, breadth-first from every coastal province.
 *    A node's depth is the smallest depth among its provinces. Depth 0 is a
 *    terminal.
 * 4. **Arrows.** Overland, value runs from the deeper node to the shallower and
 *    never between equals. Over water, it runs from the lesser market to the
 *    greater even when both stand on the same shore — because depth is hops to
 *    salt water, so every coast is depth 0, and without this rule a world built
 *    around one great inland sea would never draw a single arrow across it. The
 *    sea is the main artery of such a map, not the gap between the tributaries.
 *
 * That last rule is doing more work than it looks. Order the nodes by (depth
 * descending, market score ascending, id descending) — a total order, since the
 * last component breaks every tie. Every arrow steps strictly forward in it, so
 * the graph **cannot contain a cycle**: no proof obligation, no cycle-breaking
 * pass, and the flow can still be resolved by walking the nodes in order of
 * decreasing depth rather than by a topological sort. And because a node at
 * depth d always has a province with a neighbour at depth d − 1 in a *different*
 * node (if it were the same node the node's depth would be d − 1), **every
 * inland node has somewhere to send its value**. No orphans, no sinks that are
 * not on the water, by construction.
 *
 * The one case that needs handling is a landlocked component with no coast in
 * it at all. Those provinces would have infinite depth and no arrows would ever
 * be drawn. They get a second breadth-first pass from the lowest-indexed
 * province in the component, which stands in for the sea: an inland fair that
 * everything around it drains toward. The two guarantees above survive unchanged.
 */

import { provinceDef } from '../../data/provinces.ts';
import type { ProvinceLink } from './types.ts';
import {
  MAX_NODES,
  MIN_NODES,
  NODE_PROVINCES,
  edgeWeight,
  isWaterLink,
  type TradeNode,
} from './tradetypes.ts';

/** Minimum hops between two market seats, tried in this order until enough fit. */
const SEPARATIONS = [4, 3, 2, 1];

/**
 * Is this province on salt water? The `mapsea` seam, read defensively.
 *
 * This decides what a terminal is, and it was wrong. It used to test
 * `terrain === 'coast'`, which reads like the authoritative answer and is not:
 * `coast` is one of seven terrain values assigned by elevation and shore
 * heuristics, and `mapsea` reports that only 29 of 79 genuinely coastal
 * provinces carry it. A hill or a marsh on the shore is coastal ground and is
 * not `'coast'`. Two thirds of the coastline was therefore invisible to the
 * trade graph: those ports were scored as inland, drained to somebody else, and
 * could never be the end of a road.
 *
 * `ProvinceDef.coastal` is the authoritative flag and is what `mapsea` builds
 * its ports and lanes on. It is not on this branch yet, so this prefers it when
 * it exists and falls back to the old test until then — the same shape
 * `portLevel` uses in `trade.ts`, and for the same reason: neither branch may
 * wait for the other.
 */
function isCoastal(p: number): boolean {
  const def = provinceDef(p) as { terrain: string; coastal?: boolean };
  return typeof def.coastal === 'boolean' ? def.coastal : def.terrain === 'coast';
}

/**
 * A sea lane, structurally.
 *
 * `mapsea` owns `SeaLane` and it is not on this branch, so this names only the
 * two fields the trade graph needs. Their type is assignable to it, which is the
 * whole point: no import, no build order, and nothing to change when it lands.
 */
export interface TradeLane {
  a: number;
  b: number;
}

/**
 * Build the whole node graph. Pure: same links, same provinces, same graph.
 *
 * Called once from `createCampaign`, which also means it is called by
 * `loadCampaign` to build the fallback a short save is filled in from.
 */
export function buildTradeNodes(
  links: ProvinceLink[],
  provinceCount: number,
  lanes: readonly TradeLane[] = [],
): TradeNode[] {
  if (provinceCount <= 0) return [];
  const adjacency = buildAdjacency(links, provinceCount);
  const wanted = Math.max(
    MIN_NODES,
    Math.min(MAX_NODES, Math.round(provinceCount / NODE_PROVINCES)),
  );

  const seats = chooseSeats(adjacency, provinceCount, wanted);
  const nodeOf = assignCatchments(adjacency, provinceCount, seats);
  // Islands last, over the water, because they have no road to have been found
  // by. See `attachByLane` for why they are not simply in the adjacency.
  attachByLane(nodeOf, provinceCount, lanes);
  const depth = seaDepth(adjacency, provinceCount);

  const nodes: TradeNode[] = seats.map((seat, id) => ({
    id,
    name: provinceDef(seat).name,
    seat,
    provinces: [],
    depth: Infinity,
    outgoing: [],
    outWeight: [],
  }));
  for (let p = 0; p < provinceCount; p++) {
    const n = nodeOf[p];
    if (n < 0) continue;
    nodes[n].provinces.push(p);
    if (depth[p] < nodes[n].depth) nodes[n].depth = depth[p];
  }
  // A seat with no catchment cannot happen — a seat is always in its own — but a
  // node whose depth never got set would poison every comparison below.
  for (const n of nodes) if (!Number.isFinite(n.depth)) n.depth = 0;

  wireArrows(nodes, nodeOf, links, lanes);
  return nodes;
}

/** Province index → node index, rebuilt from the node list. */
export function nodeIndexOf(nodes: TradeNode[], provinceCount: number): number[] {
  const out = new Array<number>(provinceCount).fill(-1);
  for (const n of nodes) for (const p of n.provinces) if (p >= 0 && p < provinceCount) out[p] = n.id;
  return out;
}

// ── 1. Seats ───────────────────────────────────────────────────────────────

/**
 * What makes a market town: money, water, and walls in that order.
 *
 * The coast bonus is large on purpose. A trade graph whose terminals are inland
 * would point the arrows at nothing anybody wants, and the whole shape of the
 * realm — a hand-authored heartland on the eastern seaboard with a generated
 * frontier behind it — is an argument for the ports being the ends of the chain.
 */
export function seatScore(p: number): number {
  const def = provinceDef(p);
  return def.wealth + (isCoastal(p) ? 9 : 0) + def.fort * 2;
}

function chooseSeats(adjacency: number[][], provinceCount: number, wanted: number): number[] {
  const order: number[] = [];
  for (let p = 0; p < provinceCount; p++) order.push(p);
  order.sort((a, b) => seatScore(b) - seatScore(a) || a - b);

  for (const sep of SEPARATIONS) {
    const taken: number[] = [];
    for (const p of order) {
      if (taken.length >= wanted) break;
      if (withinHops(adjacency, p, taken, sep - 1)) continue;
      taken.push(p);
    }
    if (taken.length >= wanted) return taken.sort((a, b) => a - b);
  }

  // Separation of one was not enough to fill the quota — a very small or very
  // disconnected board. Take the best of what is left, in score order.
  const taken: number[] = [];
  for (const p of order) {
    if (taken.length >= wanted) break;
    taken.push(p);
  }
  return taken.sort((a, b) => a - b);
}

/**
 * Of two markets the same distance from the sea, whose trade runs to the other.
 *
 * The lesser market feeds the greater — a port with a small hinterland ships on
 * to the great emporium down the coast rather than the other way about. Ties go
 * to the higher id, which is arbitrary, total, and the same on every machine;
 * that last component is what makes the ordering a *total* one and is therefore
 * what the acyclicity argument in `wireArrows` rests on.
 */
function feedsTheOther(x: TradeNode, y: TradeNode): boolean {
  const sx = seatScore(x.seat);
  const sy = seatScore(y.seat);
  if (sx !== sy) return sx < sy;
  return x.id > y.id;
}

/** Is any of `others` within `limit` hops of `from`? Bounded, so it stays cheap. */
function withinHops(adjacency: number[][], from: number, others: number[], limit: number): boolean {
  if (others.length === 0) return false;
  if (others.includes(from)) return true;
  if (limit <= 0) return false;
  const target = new Set(others);
  const seen = new Set<number>([from]);
  let frontier = [from];
  for (let d = 0; d < limit; d++) {
    const next: number[] = [];
    for (const at of frontier) {
      for (const n of adjacency[at]) {
        if (seen.has(n)) continue;
        if (target.has(n)) return true;
        seen.add(n);
        next.push(n);
      }
    }
    if (next.length === 0) return false;
    frontier = next;
  }
  return false;
}

// ── 2. Catchments ──────────────────────────────────────────────────────────

/**
 * Nearest seat wins, breadth-first from all seats at once.
 *
 * Seats are seeded in ascending node order and the queue is first-in-first-out,
 * so a province equidistant from two markets joins the lower-numbered one. Every
 * province in a component containing a seat gets one; a component with no seat in
 * it gets -1 and simply does not trade, which is the honest answer for an island
 * nothing connects to.
 */
function assignCatchments(adjacency: number[][], provinceCount: number, seats: number[]): number[] {
  const out = new Array<number>(provinceCount).fill(-1);
  const queue: number[] = [];
  seats.forEach((seat, id) => {
    if (seat < 0 || seat >= provinceCount) return;
    out[seat] = id;
    queue.push(seat);
  });
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of adjacency[at]) {
      if (out[n] !== -1) continue;
      out[n] = out[at];
      queue.push(n);
    }
  }
  return out;
}

/**
 * Give the islands a market, over the water, after the land has been shared out.
 *
 * A province with no road *at all* is in nobody's catchment when catchments are
 * grown over land, so it would belong to no node, produce no trade value and
 * never be worth taking. That is a silent hole rather than a wrong answer, which
 * is the worse kind.
 *
 * Note this keys off "unassigned", never off `ProvinceDef.island` — which is
 * deliberate and turned out to matter. That flag meant "no road at all" when this
 * was written and now means "no road *home*", true of 105 of 150 provinces rather
 * than 2; an inland province on another continent has plenty of roads, none of
 * which lead to the heartland. Had this asked the flag, it would have quietly
 * started re-homing two thirds of the world by sea. The true archipelagos are
 * `neighbours(c, i).length === 0`, and they are what this actually catches.
 *
 * Lanes are deliberately *not* in the adjacency the catchments are grown from.
 * If they were, a lane would make two shores one hop apart, and a market's
 * hinterland would reach across the sea — the Midsea's far coast joining a
 * mainland fair, and two facing ports collapsing into one market because the
 * seat separation saw them as neighbours. A hinterland is land. What a lane does
 * is carry *trade between* markets, which is `wireArrows`' business, plus this:
 * attach whatever the land could not reach, to the nearest market it can sail
 * to. Breadth-first from everything already assigned, so an island chain hangs
 * off the mainland in order rather than each isle claiming the next.
 */
function attachByLane(
  nodeOf: number[],
  provinceCount: number,
  lanes: readonly TradeLane[],
): void {
  if (lanes.length === 0) return;
  const byLane: number[][] = [];
  for (let i = 0; i < provinceCount; i++) byLane.push([]);
  for (const l of lanes) {
    if (byLane[l.a] && byLane[l.b]) {
      byLane[l.a].push(l.b);
      byLane[l.b].push(l.a);
    }
  }
  for (const list of byLane) list.sort((a, b) => a - b);

  const queue: number[] = [];
  for (let p = 0; p < provinceCount; p++) if (nodeOf[p] >= 0) queue.push(p);
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of byLane[at]) {
      if (nodeOf[n] >= 0) continue;
      nodeOf[n] = nodeOf[at];
      queue.push(n);
    }
  }
}

// ── 3. Depth ───────────────────────────────────────────────────────────────

function seaDepth(adjacency: number[][], provinceCount: number): number[] {
  const depth = new Array<number>(provinceCount).fill(-1);
  const queue: number[] = [];
  for (let p = 0; p < provinceCount; p++) {
    if (!isCoastal(p)) continue;
    depth[p] = 0;
    queue.push(p);
  }
  bfs(adjacency, depth, queue);

  // Landlocked components: the lowest-indexed province stands in for the sea.
  for (let p = 0; p < provinceCount; p++) {
    if (depth[p] >= 0) continue;
    depth[p] = 0;
    bfs(adjacency, depth, [p]);
  }
  return depth;
}

function bfs(adjacency: number[][], depth: number[], queue: number[]): void {
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of adjacency[at]) {
      if (depth[n] !== -1) continue;
      depth[n] = depth[at] + 1;
      queue.push(n);
    }
  }
}

// ── 4. Arrows ──────────────────────────────────────────────────────────────

/**
 * Draw an arrow between every pair of adjacent nodes: overland, from the deeper
 * to the shallower and never between equals; over water, from the lesser market
 * to the greater even when both stand on the same shore.
 *
 * A pair may be joined by several province links; the road that carries the
 * trade is the best of them, because goods take the good road even when the army
 * took the pass.
 *
 * **Why water needs its own rule.** Depth is hops to salt water, so *every*
 * coastal node is depth 0. On a world built around one great inland sea — which
 * is what the map reference describes, every landmass facing onto the Midsea —
 * that makes every coast a terminal, and the "never between equals" rule would
 * mean no arrow is ever drawn across the sea at all. The single most important
 * artery on the board would carry nothing, and a named strait could never be
 * worth holding. Over water, then, value runs to the better market: the sea is
 * the one road where goods pass a port without stopping.
 *
 * **Acyclicity survives, and is still by construction.** Order the nodes by
 * (depth descending, market score ascending, id descending) — a total order,
 * because the last component breaks every tie. Overland arrows advance the first
 * component; sea arrows leave it equal and advance the second or third. Every
 * arrow therefore steps strictly forward in a total order, so the graph cannot
 * contain a cycle, and `runFlow` can still resolve by sorting on depth.
 * `tests/trade.test.ts` asserts this directly rather than trusting the paragraph.
 */
function wireArrows(
  nodes: TradeNode[],
  nodeOf: number[],
  links: ProvinceLink[],
  lanes: readonly TradeLane[],
): void {
  /**
   * Every road between two markets, land and sea alike.
   *
   * Sea lanes are **not** province links and must never be — `canMarch` gates on
   * `neighbours`, which reads `links`, so a lane in there would let armies march
   * across open water. `mapsea` keeps them in a parallel `CampaignState.lanes`.
   * The trade graph is the one place the two graphs are read as one thing, since
   * a merchant does not care whether the goods came by cart or by keel; they are
   * merged here, at the last moment, rather than anywhere that could leak.
   *
   * `isWaterLink` still earns its place for a *link* kind that is water without
   * being a lane — a broad river mouth or a ferry, whatever `mapsea` decides is
   * a road an army may use and a ship may also use.
   */
  const roads: Array<{ a: number; b: number; kind: number; water: boolean }> = [];
  for (const l of links) roads.push({ a: l.a, b: l.b, kind: l.kind, water: isWaterLink(l.kind) });
  for (const l of lanes) roads.push({ a: l.a, b: l.b, kind: -1, water: true });

  const best = new Map<number, number>();
  for (const l of roads) {
    const a = nodeOf[l.a] ?? -1;
    const b = nodeOf[l.b] ?? -1;
    if (a < 0 || b < 0 || a === b) continue;
    let from = nodes[a].depth > nodes[b].depth ? a : nodes[b].depth > nodes[a].depth ? b : -1;
    if (from < 0) {
      // Equal depth. Only water carries trade between two markets the same
      // distance from the sea, and it carries it toward the greater of them.
      if (!l.water) continue;
      from = feedsTheOther(nodes[a], nodes[b]) ? a : b;
    }
    const to = from === a ? b : a;
    const key = from * (MAX_NODES + 1) + to;
    const w = edgeWeight(l.kind);
    const had = best.get(key);
    if (had === undefined || w > had) best.set(key, w);
  }

  const byNode = new Map<number, Array<{ to: number; w: number }>>();
  for (const [key, w] of best) {
    const from = Math.floor(key / (MAX_NODES + 1));
    const to = key % (MAX_NODES + 1);
    const list = byNode.get(from) ?? [];
    list.push({ to, w });
    byNode.set(from, list);
  }
  for (const n of nodes) {
    const list = (byNode.get(n.id) ?? []).sort((x, y) => x.to - y.to);
    n.outgoing = list.map((e) => e.to);
    n.outWeight = list.map((e) => e.w);
  }
}

// ── Shared ─────────────────────────────────────────────────────────────────

function buildAdjacency(links: ProvinceLink[], provinceCount: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < provinceCount; i++) out.push([]);
  for (const l of links) {
    if (out[l.a] && out[l.b]) {
      out[l.a].push(l.b);
      out[l.b].push(l.a);
    }
  }
  for (const list of out) list.sort((a, b) => a - b);
  return out;
}
